<script setup lang="ts">
import { PhArrowsDownUp, PhClockCounterClockwise } from '@phosphor-icons/vue'
import { computed, shallowRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import AppScreen from '@/components/layout/AppScreen.vue'
import ScreenTabs from '@/components/layout/ScreenTabs.vue'
import MapControls from '@/components/map/MapControls.vue'
import type { MapMode } from '@/components/map/mapMode'
import MapView from '@/components/map/MapView.vue'
import EndpointPicker from '@/components/plan/EndpointPicker.vue'
import ItineraryCard from '@/components/plan/ItineraryCard.vue'
import { coordinateLabel } from '@/components/plan/plannerEndpoint'
import type { PlannedTrip, PlannerEndpoint } from '@/components/plan/plannerEndpoint'
import { Button } from '@/components/ui/button'
import { useHistoryValue } from '@/composables/useHistoryValue'
import { useMapControls } from '@/composables/useMapControls'
import { useRouteHistory } from '@/composables/useRouteHistory'
import { useRouteOptionsQuery } from '@/composables/useRouteOptionsQuery'
import { useScrollRestore } from '@/composables/useScrollRestore'
import { useTransitIndex } from '@/composables/useTransitIndex'

import { HOME_TABS } from './tabs'

const router = useRouter()
const { index } = useTransitIndex()
// One instance, shared by the pick-a-point map's control stack and the endpoint
// pickers' "use my location" — a second `useUserLocation()` would mean two
// independent geolocation requests and two `location` refs on one screen.
const {
  basemap,
  toggleBasemap,
  location,
  isLocating,
  error: locationError,
  locate,
} = useMapControls()
const { entries: history, remember } = useRouteHistory()

const start = shallowRef<PlannerEndpoint>()
const end = shallowRef<PlannerEndpoint>()
const openPicker = shallowRef<'start' | 'end'>()
const picking = shallowRef<'start' | 'end'>()
const locatingFor = shallowRef<'start' | 'end'>()
/** Only set once the user asks — typing endpoints must not fire requests. */
const searched = shallowRef<{ start: PlannerEndpoint; end: PlannerEndpoint }>()

/**
 * The three refs above, remembered per history entry as ONE value so back returns
 * the trip the user was looking at. `openPicker` / `picking` / `locatingFor` are
 * deliberately excluded: they are transient overlay state, and restoring
 * `locatingFor` would re-arm the watcher below to overwrite a restored endpoint
 * whenever geolocation later resolved.
 *
 * One object rather than three `useHistoryValue` calls, because an endpoint's
 * payload can itself be `undefined`: the store cannot tell a *cleared* endpoint
 * from one that was never written, so a per-ref restore would skip the clear and
 * leave the stale endpoint behind. Wrapping them keeps the stored value always
 * defined, which makes `{ start: undefined }` a faithful "the user cleared this".
 *
 * A writable computed so every assignment elsewhere in this file stays a plain
 * `start.value = …`; the getter allocating a fresh object per read is what makes
 * the watch inside `useHistoryValue` fire on each change.
 */
const trip = computed<PlannedTrip>({
  get: () => ({ start: start.value, end: end.value, searched: searched.value }),
  set: (next) => {
    start.value = next.start
    end.value = next.end
    searched.value = next.searched
  },
})
useHistoryValue('plan', 'trip', trip)

/**
 * The results pane scrolls, not `AppScreen`'s `<main>`, so it needs its own id.
 * Nothing is captured alongside the offset: the itineraries come from a query
 * and the recent-search list from `useRouteHistory`, both of which survive a
 * remount on their own.
 */
const { containerRef } = useScrollRestore({ id: 'plan-results' })

const allStops = computed(() => index.value?.allStops ?? [])

const routeCodes = computed(() => {
  const map = new Map<number, string>()
  for (const route of index.value?.routesByCode.values() ?? []) map.set(route.id, route.code)
  return map
})
const routeNames = computed(() => {
  const map = new Map<number, string>()
  for (const route of index.value?.routesByCode.values() ?? []) map.set(route.id, route.name)
  return map
})
/**
 * A plain mirror of `fare`, including the `0`s the extractor emits for a value it
 * could not parse. Filtering them here as well would put the "0 means unknown"
 * rule in two places, and the copy in `itineraryFare` is the one under test.
 */
const routeFares = computed(() => {
  const map = new Map<number, number>()
  for (const route of index.value?.routesByCode.values() ?? []) map.set(route.id, route.fare)
  return map
})

const query = useRouteOptionsQuery(
  computed(() => searched.value?.start),
  computed(() => searched.value?.end),
)

const itineraries = computed(() => query.data.value ?? [])
const canSearch = computed(() => Boolean(start.value && end.value))

const mode = computed<MapMode>(() => ({ kind: 'stops', stops: [] }))

/** Geolocation resolves asynchronously, so apply it to whichever field asked. */
watch(location, (point) => {
  if (!point || !locatingFor.value) return
  const endpoint: PlannerEndpoint = {
    lat: point.lat,
    lng: point.lng,
    label: 'My location',
  }
  if (locatingFor.value === 'start') start.value = endpoint
  else end.value = endpoint
  locatingFor.value = undefined
  openPicker.value = undefined
})

function set(which: 'start' | 'end', endpoint: PlannerEndpoint) {
  if (which === 'start') start.value = endpoint
  else end.value = endpoint
  openPicker.value = undefined
  picking.value = undefined
}

function requestLocation(which: 'start' | 'end') {
  locatingFor.value = which
  locate()
}

function onPickPoint(point: { lat: number; lng: number }) {
  if (!picking.value) return
  set(picking.value, {
    lat: point.lat,
    lng: point.lng,
    label: coordinateLabel(point.lat, point.lng),
  })
}

function swap() {
  const previous = start.value
  start.value = end.value
  end.value = previous
}

function search() {
  if (!start.value || !end.value) return
  searched.value = { start: start.value, end: end.value }
  remember({ start: start.value, end: end.value })
  openPicker.value = undefined
  picking.value = undefined
}

function rerun(entry: { start: PlannerEndpoint; end: PlannerEndpoint }) {
  start.value = entry.start
  end.value = entry.end
  searched.value = { start: entry.start, end: entry.end }
}

function openRoute(routeCode: string) {
  router.push({ name: 'route', params: { routeCode } })
}
</script>

<template>
  <AppScreen title="Hanoi Bus" leading="menu">
    <template #tabs>
      <ScreenTabs :tabs="HOME_TABS" />
    </template>

    <div class="flex h-full min-h-0 flex-col">
      <div class="shrink-0">
        <EndpointPicker
          marker="A"
          placeholder="Start location"
          :stops="allStops"
          :value="start"
          :open="openPicker === 'start'"
          :locating="isLocating && locatingFor === 'start'"
          :picking="picking === 'start'"
          @toggle="openPicker = openPicker === 'start' ? undefined : 'start'"
          @select="set('start', $event)"
          @use-location="requestLocation('start')"
          @start-picking="picking = 'start'"
          @clear="start = undefined"
        />
        <EndpointPicker
          marker="B"
          placeholder="End location"
          :stops="allStops"
          :value="end"
          :open="openPicker === 'end'"
          :locating="isLocating && locatingFor === 'end'"
          :picking="picking === 'end'"
          @toggle="openPicker = openPicker === 'end' ? undefined : 'end'"
          @select="set('end', $event)"
          @use-location="requestLocation('end')"
          @start-picking="picking = 'end'"
          @clear="end = undefined"
        />

        <div class="flex items-center gap-2 border-b bg-muted px-3 py-2">
          <Button
            variant="outline"
            size="sm"
            type="button"
            aria-label="Swap start and end"
            @click="swap"
          >
            <PhArrowsDownUp class="size-4" />
          </Button>
          <Button size="sm" type="button" class="flex-1" :disabled="!canSearch" @click="search">
            Search route
          </Button>
        </div>
      </div>

      <!-- Shown only while a pin is being dropped, so the map does not take
           space away from results the rest of the time. -->
      <div v-if="picking" class="relative min-h-48 flex-1 border-b">
        <MapView
          :mode="mode"
          :basemap="basemap"
          :user-location="location"
          pick-point
          @pick-point="onPickPoint"
        />

        <!-- The controls sit outside the Leaflet container, so pressing one
             cannot also drop a pin the way an in-map control would. -->
        <MapControls
          :basemap="basemap"
          :is-locating="isLocating"
          :error="locationError"
          @toggle-basemap="toggleBasemap"
          @locate="locate"
        />
      </div>

      <div v-else ref="containerRef" class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <!--
          Fetching with nothing to show is a spinner; fetching with data in hand is
          not. A restore lands inside vue-query's gc window with the itineraries
          already cached but past `staleTime`, so it refetches — and branching on
          `isFetching` alone would hide the very results back was meant to return.
        -->
        <p
          v-if="query.isFetching.value && itineraries.length === 0"
          class="p-4 text-sm text-muted-foreground"
        >
          Finding routes…
        </p>

        <p v-else-if="query.isError.value" class="p-4 text-sm text-muted-foreground">
          The journey planner is unavailable right now.
        </p>

        <template v-else-if="searched">
          <ItineraryCard
            v-for="(itinerary, index) in itineraries"
            :key="index"
            :itinerary="itinerary"
            :route-codes="routeCodes"
            :route-names="routeNames"
            :route-fares="routeFares"
            @open-route="openRoute"
          />
          <p v-if="itineraries.length === 0" class="p-4 text-sm text-muted-foreground">
            No bus route was found between these two points.
          </p>
        </template>

        <template v-else>
          <p v-if="history.length === 0" class="p-4 text-sm text-muted-foreground">
            Pick a start and an end to search for a route.
          </p>
          <template v-else>
            <p
              class="flex items-center gap-2 border-b bg-muted px-3 py-2 text-xs text-muted-foreground"
            >
              <PhClockCounterClockwise class="size-4" />
              Route history
            </p>
            <button
              v-for="(entry, index) in history"
              :key="index"
              type="button"
              class="flex w-full flex-col items-start gap-0.5 border-b px-3 py-2.5 text-left text-sm hover:bg-muted"
              @click="rerun(entry)"
            >
              <span class="truncate">{{ entry.start.label }}</span>
              <span class="truncate text-xs text-muted-foreground">→ {{ entry.end.label }}</span>
            </button>
          </template>
        </template>
      </div>
    </div>
  </AppScreen>
</template>
